<template>
    <div
        :id="id"
        ref="root"
        class="consent-list-wrapper"
        data-testid="input-consent-list"
    >
        <InputConsent
            v-for="(consent, index) in model"
            :key="index"
            :model-value="consent.selected"
            :consent="consent"
            :disabled="props.disabled || consent.disabled"
            :error="consent.error"
            :selected="consent.selected"
            @update:model-value="(val) => handleUpdate(val, index)"
            @ui:toggle-expand="emit('ui:toggle-expand', $event)"
        />
        <ErrorText
            v-if="error && typeof error === 'string'"
            :error="error"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref, watch } from 'vue'
import InputConsent from '../InputConsent/InputConsent.vue'
import ErrorText from '../ErrorText/ErrorText.vue'
import equals from 'fast-deep-equal/es6'
import type { BlockPath } from 'widget-components/integrations/CustomPage/types'
import type { Consent } from 'widget-components/types/consent'
import { autoFocusOnMounted } from 'widget-components/integrations/CustomPage/autofocus'

defineOptions({ inheritAttrs: false })

const props = defineProps<{
    id?: string
    modelValue: Consent[]
    items: Consent[]
    disabled?: boolean
    selectAll?: boolean
    error?: string
    blockPath?: BlockPath
    focus?: boolean
}>()

const emit = defineEmits(['update:modelValue', 'ui:toggle-expand'])

const model = ref<Consent[]>([])
const customPageBuilder = inject<boolean>('customPageBuilder', false)
const root = ref<HTMLElement>()

autoFocusOnMounted(
    props,
    () => root.value?.querySelector<HTMLInputElement>('input'),
)

onMounted(() => {
    if (props.selectAll) {
        model.value = props.items.map((consent) => ({ ...consent, selected: true }))
        emit('update:modelValue', model.value)
    } else {
        model.value = props.items
    }
})

if (customPageBuilder) {
    const _items = computed(() => props.items)
    watch(_items, () => {
        model.value = props.items
    }, { deep: true, immediate: true })
}

const handleUpdate = (val: boolean, index: number) => {
    model.value = model.value.map((consent, i) => {
        if (i === index) {
            return {
                ...consent,
                selected: val,
            }
        }
        return consent
    })
}

watch(
    model,
    () => {
        if (!equals(props.modelValue, model.value)) {
            emit('update:modelValue', model.value)
        }
    },
    { deep: true },
)

watch(
    () => props.modelValue,
    () => {
        model.value = props.modelValue
    },
)
</script>

<style lang="sass" scoped>
@import "InputConsentList"
</style>
